import { preserveFlagOverridesInPath } from "@hooks/useFlag";
import isExternalUrl from "@util/isExternalUrl";
import { preserveViewAsInPath } from "@util/viewAs";
import type {
  History,
  Location,
  LocationDescriptor,
  LocationDescriptorObject,
} from "history";
import { createPath, parsePath } from "history";
import { forwardRef, useMemo } from "react";
import type { LinkProps, NavLinkProps } from "react-router-dom";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import "./ViewAsPreservingLink.css";

interface ViewAsPreservingLinkProps extends Omit<LinkProps, "to"> {
  to: LocationDescriptor; // we don't support function syntax here
}

export const ViewAsPreservingLink = forwardRef<
  HTMLAnchorElement,
  ViewAsPreservingLinkProps
>(function ViewAsPreservingLink({ to, ...otherProps }, ref) {
  const location = useLocation();

  // If the URL is outside the dashboard, use a regular <a> tag and don't add the view_as_x params
  if (typeof to === "string" && isExternalUrl(to)) {
    return <a ref={ref} href={to} {...otherProps} />;
  }

  return (
    <Link ref={ref} to={preserveQueryParams(location, to)} {...otherProps} />
  );
});

interface ViewAsPreservingNavLinkProps extends Omit<NavLinkProps, "to"> {
  to: LocationDescriptor; // we don't support function syntax here
}

export const ViewAsPreservingNavLink = forwardRef<
  HTMLAnchorElement,
  ViewAsPreservingNavLinkProps
>(function ViewAsPreservingNavLink({ to, ...otherProps }, ref) {
  const location = useLocation();

  return (
    <NavLink ref={ref} to={preserveQueryParams(location, to)} {...otherProps} />
  );
});

/**
 * Preserves the flag overrides and view_as parameters in the query string when navigating to a new path.
 * @param to The target path.
 * @returns The target path with the flag overrides and view_as parameters preserved.
 */
export function preserveQueryParams(
  location: Location,
  to: ViewAsPreservingLinkProps["to"],
): LocationDescriptorObject {
  const locationObj = typeof to === "string" ? parsePath(to) : to;

  const currentPath = createPath(location);
  const nextPath = createPath(locationObj);

  const pathWithFlagOverrides = preserveFlagOverridesInPath(
    currentPath,
    nextPath,
  );
  const pathWithViewAs = preserveViewAsInPath(
    currentPath,
    pathWithFlagOverrides,
  );

  const [pathname, search] = pathWithViewAs.split("?");

  return { ...locationObj, pathname, search };
}

/**
 * Returns a new history object with the push and replace methods that preserve the flag overrides
 * and view_as parameters in the query string when navigating to a new path.
 */
export const useViewAsPreservingHistory = (): History => {
  const history = useHistory();

  return useMemo(() => {
    const push = (location: LocationDescriptor) => {
      history.push(preserveQueryParams(history.location, location));
    };

    const replace = (location: LocationDescriptor) => {
      history.replace(preserveQueryParams(history.location, location));
    };

    return { ...history, push, replace };
  }, [history]);
};
